// ============================================================================
// THE CYCLE — Settings Context
// Persistent user preferences for display, behavior, and UI customization.
// All settings are stored in localStorage and available app-wide.
// ============================================================================

import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'the-cycle-settings';

// Default settings
const DEFAULT_SETTINGS = {
  // Display
  displayDensity: 'comfortable',    // 'compact' | 'comfortable' | 'spacious'
  defaultCategory: 'batting',       // 'batting' | 'pitching'
  rowsPerPage: 25,                  // 10 | 25 | 50 | 100
  showGrades: true,                 // Show letter grades on stat values
  
  // Behavior
  animationsEnabled: true,          // Page transitions and micro-interactions
  autoRefreshScores: true,          // Auto-refresh live scores
  autoRefreshInterval: 30,          // Seconds between score refreshes
  
  // Advanced
  showAdvancedStats: true,          // Show advanced sabermetric columns by default
  colorCodeStats: true,             // Heat-color stat cells by percentile
};

/**
 * Read persisted settings from localStorage, merge with defaults.
 */
function getPersistedSettings() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch {
    // localStorage may be unavailable
  }
  return { ...DEFAULT_SETTINGS };
}

const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
  const [settings, setSettingsState] = useState(() => getPersistedSettings());

  // Persist settings changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Silently ignore storage errors
    }
  }, [settings]);

  // Update a single setting
  const setSetting = useCallback((key, value) => {
    setSettingsState(prev => ({ ...prev, [key]: value }));
  }, []);

  // Update multiple settings at once
  const setSettings = useCallback((updates) => {
    setSettingsState(prev => ({ ...prev, ...updates }));
  }, []);

  // Reset all settings to defaults
  const resetSettings = useCallback(() => {
    setSettingsState({ ...DEFAULT_SETTINGS });
  }, []);

  // Density-based spacing helpers
  const densitySpacing = useMemo(() => {
    switch (settings.displayDensity) {
      case 'compact':
        return { tablePy: 0.25, cardPadding: 1.5, gap: 1, fontSize: '0.8rem' };
      case 'spacious':
        return { tablePy: 0.75, cardPadding: 3, gap: 2.5, fontSize: '0.95rem' };
      default: // comfortable
        return { tablePy: 0.5, cardPadding: 2, gap: 1.5, fontSize: '0.875rem' };
    }
  }, [settings.displayDensity]);

  const value = useMemo(() => ({
    ...settings,
    setSetting,
    setSettings,
    resetSettings,
    densitySpacing,
    DEFAULT_SETTINGS,
  }), [settings, setSetting, setSettings, resetSettings, densitySpacing]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;
